import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BeneficiarySelector } from '@/shared/components/cse/BeneficiarySelector'
import { FrenchDateInput } from '@/shared/components/cse/FrenchDateInput'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { NativeSelect } from '@/shared/components/ui/native-select'
import type { OccupantInput } from '@/features/reservation/schemas/wizardOccupants.schema'
import type { BeneficiaryRow } from '@/shared/schemas/beneficiariesApi.schema'

type AgeMode = 'off' | 'age_band' | 'birthdate'
type AgeBand = 'baby' | 'child' | 'teen' | 'adult'

export type OccupantsFieldProps = {
  occupants: OccupantInput[]
  beneficiaries: BeneficiaryRow[]
  capacityMax: number | null
  ageMode?: AgeMode
  /** T6.24: independent adult/child caps (already net of the employee occupant); null = no cap. */
  maxAdults?: number | null
  maxChildren?: number | null
  onChange: (next: OccupantInput[]) => void
}

const AGE_BANDS: AgeBand[] = ['baby', 'child', 'teen', 'adult']

function adhocOccupants(occupants: OccupantInput[]): OccupantInput[] {
  return occupants.filter((o) => o.source === 'adhoc')
}

function selectedBeneficiaryIds(occupants: OccupantInput[]): string[] {
  return occupants.flatMap((o) => (o.source === 'beneficiary' ? [o.beneficiary_id] : []))
}

// Mirrors the server default mapping (only "adult" counts as adult).
function bandCategory(band?: AgeBand): 'adult' | 'child' {
  return band === 'adult' || band === undefined ? 'adult' : 'child'
}

// Mirrors AgeBand::fromBirthDate (backend): baby 0-2, child 3-13, teen 14-17, adult 18+.
function fullYearsBetween(from: Date, to: Date): number {
  let years = to.getFullYear() - from.getFullYear()
  const beforeAnniversary =
    to.getMonth() < from.getMonth() ||
    (to.getMonth() === from.getMonth() && to.getDate() < from.getDate())
  if (beforeAnniversary) years -= 1
  return years
}

// No birth date on file -> adult (conservative default, mirrors BuildReservationOccupantsService).
function ageBandFromDateNaissance(dateNaissance: string | null | undefined): AgeBand {
  if (!dateNaissance) return 'adult'
  const years = fullYearsBetween(new Date(dateNaissance), new Date())
  if (years <= 2) return 'baby'
  if (years <= 13) return 'child'
  if (years <= 17) return 'teen'
  return 'adult'
}

/**
 * Wizard occupants picker: checkbox selection of registered beneficiaries (none pre-checked)
 * plus optional one-off persons. The booking employee is added server-side (occupant #1).
 */
export function OccupantsFieldView({
  occupants,
  beneficiaries,
  capacityMax,
  ageMode = 'off',
  maxAdults = null,
  maxChildren = null,
  onChange,
}: OccupantsFieldProps) {
  const { t } = useTranslation()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [ageBand, setAgeBand] = useState<AgeBand | ''>('')

  const adhoc = useMemo(() => adhocOccupants(occupants), [occupants])
  const beneficiaryIds = useMemo(() => selectedBeneficiaryIds(occupants), [occupants])

  const selectableBeneficiaries = useMemo(
    () => beneficiaries.filter((b) => b.admission_status !== 'refused'),
    [beneficiaries],
  )

  const atCapacity = capacityMax !== null && occupants.length >= capacityMax
  const remainingSlots =
    capacityMax === null ? null : Math.max(0, capacityMax - adhoc.length)

  const beneficiaryCategory = (beneficiaryId: string): 'adult' | 'child' => {
    const row = beneficiaries.find((b) => b.id === beneficiaryId)
    return bandCategory(ageBandFromDateNaissance(row?.date_naissance))
  }

  // Combines ad-hoc entries (typed age_band) and registered beneficiaries (age from
  // date_naissance) into the same 2 counters - mirrors assertAdultChildCapacity server-side.
  const occupantCounts = occupants.reduce(
    (acc, o) => {
      const category = o.source === 'adhoc' ? bandCategory(o.age_band) : beneficiaryCategory(o.beneficiary_id)
      acc[category] += 1
      return acc
    },
    { adult: 0, child: 0 },
  )
  const showAgeCounters = ageMode === 'age_band' && (maxAdults !== null || maxChildren !== null)
  const selectedCategory = ageMode === 'age_band' && ageBand !== '' ? bandCategory(ageBand) : null
  const atAdultCapacity =
    maxAdults !== null && selectedCategory === 'adult' && occupantCounts.adult >= maxAdults
  const atChildCapacity =
    maxChildren !== null && selectedCategory === 'child' && occupantCounts.child >= maxChildren

  const syncBeneficiarySelection = (ids: string[]): void => {
    const nextAdhoc = adhocOccupants(occupants)
    const nextBeneficiary = ids.map(
      (beneficiary_id): OccupantInput => ({ source: 'beneficiary', beneficiary_id }),
    )
    onChange([...nextBeneficiary, ...nextAdhoc])
  }

  const addAdhoc = (): void => {
    if (atCapacity || firstName.trim() === '' || lastName.trim() === '' || atAdultCapacity || atChildCapacity) return
    onChange([
      ...occupants,
      {
        source: 'adhoc',
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        birth_date: ageMode === 'birthdate' && birthDate !== '' ? birthDate : undefined,
        age_band: ageMode === 'age_band' && ageBand !== '' ? ageBand : undefined,
      },
    ])
    setFirstName('')
    setLastName('')
    setBirthDate('')
    setAgeBand('')
  }

  const removeAdhoc = (index: number): void => {
    const nextAdhoc = adhoc.filter((_, i) => i !== index)
    const nextBeneficiary = beneficiaryIds.map(
      (beneficiary_id): OccupantInput => ({ source: 'beneficiary', beneficiary_id }),
    )
    onChange([...nextBeneficiary, ...nextAdhoc])
  }

  const labelForAdhoc = (o: OccupantInput): string => {
    if (o.source !== 'adhoc') {
      return ''
    }
    return `${o.first_name} ${o.last_name}`
  }

  return (
    <section className="flex flex-col gap-4" aria-label={t('reservation.wizard.occupants.title')}>
      <div>
        <h3 className="text-sm font-medium">{t('reservation.wizard.occupants.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('reservation.wizard.occupants.intro')}</p>
      </div>

      <BeneficiarySelector
        beneficiaries={selectableBeneficiaries}
        selectedIds={beneficiaryIds}
        maxSelectable={remainingSlots}
        disabled={atCapacity && beneficiaryIds.length === 0}
        idPrefix="occ-beneficiary"
        onChange={syncBeneficiarySelection}
        helpText={t('shared.beneficiary_selector.children_help')}
      />

      {atCapacity ? (
        <p className="text-sm text-muted-foreground">{t('reservation.wizard.occupants.at_capacity')}</p>
      ) : null}

      {showAgeCounters ? (
        <p className="text-sm text-muted-foreground" role="status">
          {maxAdults !== null
            ? t('reservation.wizard.occupants.adults_count', { count: occupantCounts.adult, max: maxAdults })
            : null}
          {maxAdults !== null && maxChildren !== null ? ' · ' : null}
          {maxChildren !== null
            ? t('reservation.wizard.occupants.children_count', { count: occupantCounts.child, max: maxChildren })
            : null}
        </p>
      ) : null}
      {atAdultCapacity ? (
        <p className="text-sm text-muted-foreground">{t('reservation.wizard.occupants.at_adult_capacity')}</p>
      ) : null}
      {atChildCapacity ? (
        <p className="text-sm text-muted-foreground">{t('reservation.wizard.occupants.at_child_capacity')}</p>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label>{t('reservation.wizard.occupants.add_person')}</Label>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1 space-y-1.5">
            <Label htmlFor="adhoc-first-name">{t('reservation.wizard.occupants.first_name')}</Label>
            <Input
              id="adhoc-first-name"
              autoComplete="off"
              value={firstName}
              disabled={atCapacity}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="min-w-0 flex-1 space-y-1.5">
            <Label htmlFor="adhoc-last-name">{t('reservation.wizard.occupants.last_name')}</Label>
            <Input
              id="adhoc-last-name"
              autoComplete="off"
              value={lastName}
              disabled={atCapacity}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          {ageMode === 'birthdate' ? (
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="adhoc-birth-date">{t('reservation.wizard.occupants.birth_date')}</Label>
              <FrenchDateInput
                id="adhoc-birth-date"
                value={birthDate}
                disabled={atCapacity}
                onChange={setBirthDate}
              />
            </div>
          ) : null}
          {ageMode === 'age_band' ? (
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="adhoc-age-band">{t('reservation.wizard.occupants.age_band')}</Label>
              <NativeSelect
                id="adhoc-age-band"
                value={ageBand}
                disabled={atCapacity}
                onChange={(e) => setAgeBand(e.target.value as AgeBand | '')}
              >
                <option value="">{t('reservation.wizard.occupants.age_band_placeholder')}</option>
                {AGE_BANDS.map((band) => (
                  <option key={band} value={band}>
                    {t(`reservation.wizard.occupants.age_band_option.${band}`)}
                  </option>
                ))}
              </NativeSelect>
            </div>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            disabled={
              atCapacity || firstName.trim() === '' || lastName.trim() === '' || atAdultCapacity || atChildCapacity
            }
            onClick={addAdhoc}
          >
            {t('reservation.wizard.occupants.add')}
          </Button>
        </div>
      </div>

      {adhoc.length > 0 ? (
        <div className="space-y-2">
          <Label>{t('reservation.wizard.occupants.selected_adhoc')}</Label>
          <ul className="flex flex-col gap-1">
            {adhoc.map((o, index) => (
              <li key={index} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <span>{labelForAdhoc(o)}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeAdhoc(index)}>
                  {t('reservation.wizard.occupants.remove')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  )
}
